import React, { useState } from 'react';
import { Dropdown, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
// PERBAIKAN: Menggunakan path import yang benar
import { getInitials } from '../../../../shared/utils/getInitials';
import PaginationComponent from '../../../../shared/components/pagination/PaginationComponent';
import { IconUsers, IconAlertTriangle } from '@tabler/icons-react';
import RequireAuth from '../../../../auth/middleware/RequireAuth';
import { useAuth } from '../../../../auth/context/AuthProvider';

// ====================================================================
// Komponen Utama: PropertiesTable
// ====================================================================

interface PropertiesTableProps {
    properties: any;
    currentPage: number;
    onPageChange: (page: number) => void;
    onRemove: (property: any) => void;
    onUpdate: (id: string) => void;
    onAssign: (id: string) => void;
    onBankAccounts: (id: string) => void;
    onToggleActive: (property: { id: string; name: string; is_property_active?: boolean }, next: boolean) => void;
    togglingId: string | null;
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
}

const getPropertyBillingType = (billing_type: any): string => {
    const billingTypeMap: { [key: string]: string } = {
        'vacation_rental': 'Vacation Rental',
        'hotel': 'Hotel'
    };

    const billingTypeSlug = billing_type;

    return billingTypeMap[billingTypeSlug] || billingTypeSlug;
}

// Rough size heuristic to flag a billing_type that likely doesn't match the
// property's actual room inventory anymore (e.g. a "hotel" with only a
// handful of rooms, or a "vacation_rental" that has grown past hotel size).
// Not a hard rule — just a nudge to double-check the classification.
const ROOM_COUNT_THRESHOLD = 14;

const getBillingTypeMismatch = (billing_type: string | null | undefined, activeRoomsCount?: number): string | null => {
    if (activeRoomsCount === undefined || activeRoomsCount === null) return null;

    if (billing_type === 'hotel' && activeRoomsCount < ROOM_COUNT_THRESHOLD) {
        return `This property is billed as Hotel but only has ${activeRoomsCount} active room(s) (under ${ROOM_COUNT_THRESHOLD}). Consider changing its billing type to Vacation Rental.`;
    }
    if (billing_type === 'vacation_rental' && activeRoomsCount > ROOM_COUNT_THRESHOLD) {
        return `This property is billed as Vacation Rental but has ${activeRoomsCount} active room(s) (over ${ROOM_COUNT_THRESHOLD}). Consider changing its billing type to Hotel.`;
    }
    return null;
}

// room_types.total_rooms is a separate, independently-maintained figure (only
// recalculated when a room is added/closed via the Room Number module) that can
// drift from the live active-room count — see the September billing cross-check
// that found this on ~1 in 5 vacation_rental properties.
const getRoomCountMismatch = (activeRoomsCount?: number, roomTypesTotalRooms?: number | null): string | null => {
    if (activeRoomsCount === undefined || roomTypesTotalRooms === undefined || roomTypesTotalRooms === null) return null;
    if (activeRoomsCount === roomTypesTotalRooms) return null;

    return `Room count mismatch: ${activeRoomsCount} active room(s) in Room Number vs ${roomTypesTotalRooms} in Room Type's total_rooms. These should match — check Room Type setup for this property.`;
}

const PropertiesTable: React.FC<PropertiesTableProps> = ({
    properties,
    currentPage,
    onPageChange,
    onRemove,
    onUpdate,
    onAssign,
    onBankAccounts,
    onToggleActive,
    togglingId,
    isFetching,
    isError
    // isSuccess tidak lagi diperlukan secara eksplisit untuk logika render utama
}) => {

    const { user } = useAuth();
    // Billing connections are dev-only data; the API only sends billing_connected to dev.
    const isDev = !!user?.role?.includes('dev');
    const columnCount = isDev ? 11 : 10;

    const [showUsersDialog, setShowUsersDialog] = useState<boolean>(false);
    const [selectedPropertyForUsers, setSelectedPropertyForUsers] = useState<any | null>(null);

    const handleShowUsers = (property: any) => {
        setSelectedPropertyForUsers(property);
        setShowUsersDialog(true);
    };

    const handleCloseUsers = () => {
        setShowUsersDialog(false);
        setSelectedPropertyForUsers(null);
    };

    // Variabel aman untuk diakses
    const tableData = properties?.data ?? [];

    console.log('PropertiesTable - tableData:', properties?.data);
    const paginationData = properties?.data?.pagination;

    // Fungsi untuk merender konten body tabel berdasarkan state
    const renderTableBody = () => {
        // Kondisi 1: Error
        if (isError) {
            return (
                <tr>
                    <td colSpan={columnCount} className="text-center p-4 text-danger">
                        Failed to load properties. Please try again.
                    </td>
                </tr>
            );
        }

        // Kondisi 2: Tidak ada data (dan tidak sedang fetching)
        if (!isFetching && tableData.length === 0) {
            return (
                <tr>
                    <td colSpan={columnCount} className="text-center p-4 text-muted">
                        No properties found.
                    </td>
                </tr>
            );
        }

        // Kondisi 3: Ada data, render baris-baris tabel
        return tableData.map((property: any, index: number) => (
            <tr key={property.id}>
                <td className="text-muted">{index + 1}</td>
                <td className="fw-medium">{property.name}</td>
                <td>
                    <BillingTypeCell billingType={property.billing_type} activeRoomsCount={property.active_rooms_count} />
                </td>
                <td className="text-center">
                    <TotalRoomCell activeRoomsCount={property.active_rooms_count} roomTypesTotalRooms={property.room_types_total_rooms} />
                </td>
                <td>{property.region?.name || property.region_id || '-'}</td>
                <td>
                    <GroupCell groups={property.groups} />
                </td>
                {isDev && (
                    <td>
                        <BillingConnectionCell connected={property.billing_connected} via={property.billing_connected_via} />
                    </td>
                )}
                <td>
                    {property.street_address && property.street_address.length > 35
                        ? `${property.street_address.substring(0, 35)}...`
                        : property.street_address}
                </td>
                <td>
                    {property.users && property.users.length > 0 ? (
                        <UserAvatars
                            users={property.users}
                            onViewMore={() => handleShowUsers(property)}
                        />
                    ) : (
                        <span className="text-muted">No users assigned</span>
                    )}
                </td>
                <td>
                    <RequireAuth
                        requiredRoles={['dev']}
                        requiredPermissions={['property_management_activate']}
                        asComponent
                        fallback={
                            <span className={`badge ${property.is_property_active ? 'bg-success' : 'bg-secondary'}`}>
                                {property.is_property_active ? 'Active' : 'Inactive'}
                            </span>
                        }
                    >
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`activate-${property.id}`}
                                checked={!!property.is_property_active}
                                disabled={togglingId === property.id}
                                onChange={(e) => onToggleActive(property, e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor={`activate-${property.id}`}>
                                {property.is_property_active ? 'Active' : 'Inactive'}
                            </label>
                        </div>
                    </RequireAuth>
                </td>
                <td className="text-end">
                    <Dropdown>
                        <Dropdown.Toggle variant="default">Actions</Dropdown.Toggle>
                        {/* strategy 'fixed': .table-responsive (overflow auto) otherwise clips the menu
                            when the list is short (e.g. a single filtered row). */}
                        <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_update']} asComponent>
                                <Dropdown.Item onClick={() => onUpdate(property.id)}>Edit</Dropdown.Item>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_view']} asComponent>
                                <Dropdown.Item onClick={() => onBankAccounts(property.id)}>Bank Accounts</Dropdown.Item>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_assign_user']} asComponent>
                                <Dropdown.Item onClick={() => onAssign(property.id)}>Select User</Dropdown.Item>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['property_management_delete']} asComponent>
                                <Dropdown.Item onClick={() => onRemove(property)}>
                                    <span className="text-danger">Delete</span>
                                </Dropdown.Item>
                            </RequireAuth>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
            </tr>
        ));
    };

    return (
        <>
            <div className="table-responsive position-relative">
                {isFetching && (
                    <div className="spinner-overlay table-spinner-overlay active" style={{ zIndex: 10 }}>
                        <div className="spinner-border table-spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
                <table className="table table-hover table-vcenter">
                    <thead>
                        <tr>
                            <th>No</th>
                            <th>Title</th>
                            <th>Billing Type</th>
                            <th>Total Room</th>
                            <th>Region</th>
                            <th>Groups</th>
                            {isDev && <th>Billing Connection</th>}
                            <th>Location</th>
                            <th>Users</th>
                            <th>Status</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderTableBody()}
                    </tbody>
                </table>
            </div>

            {/* Pagination hanya muncul jika tidak error, tidak fetching, dan ada data */}
            {!isError && !isFetching && paginationData && tableData.length > 0 && (
                <PaginationComponent
                    totalPages={paginationData.last_page}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                />
            )}

            {selectedPropertyForUsers && (
                <ViewUsersDialog
                    show={showUsersDialog}
                    onClose={handleCloseUsers}
                    property={selectedPropertyForUsers}
                />
            )}
        </>
    );
};

export default PropertiesTable;

// --- Komponen Helper ---

interface BillingTypeCellProps {
    billingType?: string | null;
    activeRoomsCount?: number;
}

const BillingTypeCell: React.FC<BillingTypeCellProps> = ({ billingType, activeRoomsCount }) => {
    const label = getPropertyBillingType(billingType);
    const mismatchMessage = getBillingTypeMismatch(billingType, activeRoomsCount);

    if (!mismatchMessage) {
        return <span>{label}</span>;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{mismatchMessage}</Tooltip>}>
            <span className="text-warning d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                <IconAlertTriangle size={16} />
                {label}
            </span>
        </OverlayTrigger>
    );
};

interface TotalRoomCellProps {
    activeRoomsCount?: number;
    roomTypesTotalRooms?: number | null;
}

const TotalRoomCell: React.FC<TotalRoomCellProps> = ({ activeRoomsCount, roomTypesTotalRooms }) => {
    if (activeRoomsCount === undefined) {
        return <span>-</span>;
    }

    const mismatchMessage = getRoomCountMismatch(activeRoomsCount, roomTypesTotalRooms);
    if (!mismatchMessage) {
        return <span>{activeRoomsCount}</span>;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{mismatchMessage}</Tooltip>}>
            <span className="text-warning d-inline-flex align-items-center justify-content-center gap-1" style={{ cursor: 'help' }}>
                <IconAlertTriangle size={16} />
                {activeRoomsCount}
            </span>
        </OverlayTrigger>
    );
};

// Satu property seharusnya cuma punya 1 group (whitelabel). Tandai dengan jelas
// kalau ada property yang tidak punya group sama sekali atau malah punya lebih
// dari 1 — kedua kondisi ini butuh dibereskan lewat form Update Property.
interface GroupCellProps {
    groups?: { id: string; name: string }[] | null;
}

const GroupCell: React.FC<GroupCellProps> = ({ groups }) => {
    const count = groups?.length ?? 0;

    if (count === 1) {
        return <span>{groups![0].name}</span>;
    }

    const isTooMany = count > 1;
    const label = isTooMany
        ? groups!.map((group) => group.name).join(", ")
        : "No group assigned";
    const tooltipMessage = isTooMany
        ? `This property belongs to ${count} groups. A property should belong to exactly 1 group — please fix this in Update Property.`
        : "This property has no group assigned. A property should belong to exactly 1 group — please assign one in Update Property.";

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltipMessage}</Tooltip>}>
            <span className="text-danger d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                <IconAlertTriangle size={16} />
                {label}
            </span>
        </OverlayTrigger>
    );
};

// Dev-only. "Connected" = billing can pull this property: a property-scoped connection, or a
// company-scoped one on the company that owns the property's group. Undefined = old backend.
interface BillingConnectionCellProps {
    connected?: boolean;
    via?: 'property' | 'company' | null;
}

const BillingConnectionCell: React.FC<BillingConnectionCellProps> = ({ connected, via }) => {
    if (connected === undefined) {
        return <span className="text-muted">-</span>;
    }
    if (!connected) {
        return <span className="badge bg-secondary text-white">Not connected</span>;
    }

    return (
        <div>
            <span className="badge bg-success text-white">Connected</span>
            <div className="small text-muted">{via === 'company' ? 'via company' : 'via property'}</div>
        </div>
    );
};

interface UserAvatarsProps {
    users: any[];
    limit?: number;
    onViewMore: () => void;
}

const UserAvatars: React.FC<UserAvatarsProps> = ({ users, limit = 4, onViewMore }) => {
    const visibleUsers = users.slice(0, limit);
    const hiddenUsersCount = users.length - limit;

    return (
        <div className="avatar-list avatar-list-stacked">
            {visibleUsers.map((user) => (
                <OverlayTrigger key={user.id} placement="bottom" overlay={<Tooltip id={`tooltip-${user.id}`}>{user.name}</Tooltip>}>
                    <span className="avatar rounded-circle">{getInitials(user.name)}</span>
                </OverlayTrigger>
            ))}
            {hiddenUsersCount > 0 && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-more">{`View ${hiddenUsersCount} more users`}</Tooltip>}>
                    <span className="avatar rounded-circle bg-secondary-lt" onClick={onViewMore} style={{ cursor: 'pointer' }}>
                        +{hiddenUsersCount}
                    </span>
                </OverlayTrigger>
            )}
        </div>
    );
};

interface ViewUsersDialogProps {
    show: boolean;
    onClose: () => void;
    property: any;
}

const ViewUsersDialog: React.FC<ViewUsersDialogProps> = ({ show, onClose, property }) => {
    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="d-flex align-items-center">
                        <IconUsers size={24} className="me-2 text-primary" />
                        <span>Users Assigned to '{property.name}'</span>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {property.users && property.users.length > 0 ? (
                    <ul className="list-group list-group-flush">
                        {property.users.map((user: any) => (
                            <li key={user.id} className="list-group-item d-flex align-items-center">
                                <span className="avatar rounded-circle me-3">{getInitials(user.name)}</span>
                                <span className="fw-medium">{user.name}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-muted text-center">No users are assigned to this property.</p>
                )}
            </Modal.Body>
        </Modal>
    );
};